import React, { useCallback, useMemo, FC } from 'react';
import Draggable, { DraggableData } from 'react-draggable';
import { ECGPlot } from 'components';
import { useAppSelector, useRef, useState, useEffect } from 'hooks';
import { ECG_DOWNLOAD_STROKE, ECG_FROM_DB_STROKE } from './constants';
import styles from './styles.module.sass';
import { getIntSamples, getPlotWidth } from './helpers/helpers';
import { ECG_PLOT_WIDTH, PIXELS_IN_PLOT_SECOND } from 'utils/constants';
import { StudyItemController } from './components/components';

type Props = {
  onAlignmentEnd: () => void;
};

export const ECGCompareItem: FC<Props> = ({ onAlignmentEnd }) => {
  const { dbECGData: everbeatData, downloadECGData: referenceData } = useAppSelector(({ study }) => study);
  const { firstName } = useAppSelector(({ user }) => user.data);
  const [downloadECGOffset, setDownloadECGOffset] = useState(0);
  const [scrollLeftValue, setScrollLeftValue] = useState(0);
  const dbECGref = useRef(null);
  const downloadECGref = useRef(null);
  const subjectId = firstName;
  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    setScrollLeftValue(e.currentTarget.scrollLeft);
  };
  const handleOffsetChange = (_, draggable: DraggableData) => {
    setDownloadECGOffset(draggable.x + scrollLeftValue);
  };

  useEffect(() => {
    if (referenceData?.offset) {
      setDownloadECGOffset(-(referenceData.offset * PIXELS_IN_PLOT_SECOND));
    }
  }, [referenceData]);

  const getComparedSamples = useCallback(() => {
    const samplesInPixels = everbeatData.samples.length / ECG_PLOT_WIDTH;
    let intEverbeatSamples: number[], intReferenceSamples: number[];
    if (downloadECGOffset > 0) {
      const everbeatOffset = Math.round(downloadECGOffset * samplesInPixels);
      intEverbeatSamples = getIntSamples(everbeatData.samples, everbeatOffset);
      intReferenceSamples = getIntSamples(referenceData.samples);
    } else {
      intEverbeatSamples = getIntSamples(everbeatData.samples);
      const referenceOffset = Math.round(Math.abs(downloadECGOffset) * samplesInPixels);
      intReferenceSamples = getIntSamples(referenceData.samples, referenceOffset);
    }

    return { intEverbeatSamples, intReferenceSamples };
  }, [everbeatData, referenceData, downloadECGOffset]);

  const everbeatPlotWidth = useMemo(() => getPlotWidth(everbeatData), [everbeatData]);
  const referencePlotWidth = useMemo(() => getPlotWidth(referenceData), [referenceData]);
  const referencePlotXOffset = useMemo(() => downloadECGOffset - scrollLeftValue, [downloadECGOffset, scrollLeftValue]);

  return (
    <div className={styles.container}>
      <div className={styles.ecg_wrapper}>
        <div ref={dbECGref} className={styles.plot} onScroll={handleScroll}>
          <ECGPlot
            strokeColor={ECG_FROM_DB_STROKE}
            samples={everbeatData.samples}
            // drawChart={150}
            decreaseWave={0}
            // rate: 256 - good looking, 512 - real
            sampleRate={everbeatData.sampleRate}
            // duration to show 5 real, 9 - fit good
            // durationToShow={15}
            durationToShow={undefined}
            // volts 5 - good-looking, 0.5 - real
            // millivolts={2}
            millivolts={undefined}
            // time 7 good-looking, 10 - real
            startTime={0}
            showGrid={true}
            signalStrokeWidth={1}
            smallGridStrokeWidth={undefined}
            bigGridStrokeWidth={undefined}
            // real : 1
            physicalGridSquareSize={1}
            full={undefined}
            backgroundFill='#fff'
            plotWidth={everbeatPlotWidth}
          />
        </div>
        {referenceData && (
          <>
            <div className={styles.mask} />
            <Draggable
              axis='x'
              nodeRef={downloadECGref}
              onStop={handleOffsetChange}
              position={{ x: referencePlotXOffset, y: 0 }}
            >
              <div ref={downloadECGref} className={styles.download_ecg}>
                <ECGPlot
                  strokeColor={ECG_DOWNLOAD_STROKE}
                  samples={referenceData.samples}
                  // drawChart={150}
                  decreaseWave={0}
                  // rate: 256 - good looking, 512 - real
                  sampleRate={referenceData.sampleRate}
                  // duration to show 5 real, 9 - fit good
                  // durationToShow={15}
                  durationToShow={undefined}
                  // volts 5 - good-looking, 0.5 - real
                  // millivolts={2}
                  millivolts={undefined}
                  // time 7 good-looking, 10 - real
                  startTime={0}
                  showGrid={false}
                  signalStrokeWidth={1}
                  smallGridStrokeWidth={undefined}
                  bigGridStrokeWidth={undefined}
                  // real : 1
                  physicalGridSquareSize={1}
                  full={undefined}
                  backgroundFill='#fff'
                  plotWidth={referencePlotWidth}
                />
              </div>
            </Draggable>
          </>
        )}
      </div>
      <StudyItemController
        getComparedSamples={getComparedSamples}
        everbeatData={everbeatData}
        referenceData={referenceData}
        subjectId={subjectId}
        onAlignmentEnd={onAlignmentEnd}
      />
    </div>
  );
};
